import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { Account } from "../../../../../shared/domains/account/account";
import { redirectionHandledOnAccountBlocked } from "../../../../../shared/domains/account/account";
import {
  isAccountBlocked,
  isRecipientNotFoundByInvalidPhoneErrorResponse,
} from "../../../../../shared/domains/pincode/pincode-error";
import { TransferMode } from "../../../../../shared/domains/transactions/cash-transfer/transfer-mode";
import type { TransactionRequest } from "../../../../../shared/domains/transactions/transaction-request";
import { PincodeState } from "../../../../machine/keyboard-machine-type";
import { TransferState, useTransferMachine } from "../../../../machine/transfer-machine";
import { ErrorMessage } from "../../../common/error-message";
import { PincodeKeyboard } from "../../../common/keyboard/pincode-keyboard";
import { MainColorSpinner } from "../../../common/spinner";
import { AmountSelection } from "../../../common/transaction/amount-selection";
import { theme } from "../../../styles/theme";
import { SourceAndDestination, SourceAndDestinationEnum } from "./source-and-destination";
import { Subtitle, TransferSummary } from "./transfer-summary";

export interface SimpleTransferModalProps {
  closeModal?: () => void;
}

const loadingState = [TransferState.RequestingTransfer, PincodeState.PincodeConfirmation];

export const SimpleTransferModal = (props: SimpleTransferModalProps) => {
  const { closeModal } = props;
  const { context, state, selectAmount, submitPincode } = useTransferMachine(true);
  const history = useHistory();
  const [editingSourceAndDestination, setEditingSourceAndDestination] = useState<SourceAndDestinationEnum | null>(null);
  const [sourceAccount, setSourceAccount] = useState<Account | null>(null);

  useEffect(() => {
    if (redirectionHandledOnAccountBlocked(context.error, history.push)) {
      closeModal?.();
    }
  }, [context.error, history.push, closeModal]);

  const { formatMessage } = useIntl();
  const errorPhoneMessage = formatMessage("simpleTransfer.invalidPhoneNumber");
  let errorMessage: string | undefined;
  if (isRecipientNotFoundByInvalidPhoneErrorResponse(context.error)) {
    errorMessage = errorPhoneMessage;
  } else if (!isAccountBlocked(context.error)) {
    errorMessage = context.error;
  } else {
    errorMessage = undefined;
  }

  const handleSetSourceAccount = (account: Account) => {
    setSourceAccount(account);
    setEditingSourceAndDestination(null);
  };

  const onOpenSourceAccountSelection = () => {
    setEditingSourceAndDestination(SourceAndDestinationEnum.Source);
  };
  switch (state) {
    case TransferState.SelectingAmount:
    case TransferState.RequestingTransfer:
    case TransferState.RequestingTransferError:
    case PincodeState.PincodeConfirmation:
    case TransferState.ConfirmTransferError: {
      return (
        <>
          <Centered>{errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}</Centered>
          <StyledAmountSelection
            sourceAccount={sourceAccount}
            isP2PSimpleTransfer
            submitAmount={selectAmount}
            loading={loadingState.includes(state)}
            editingSourceAndDestination={editingSourceAndDestination}
            onOpenSourceAccountSelection={onOpenSourceAccountSelection}
          >
            {editingSourceAndDestination === SourceAndDestinationEnum.Source && (
              <SourceAndDestination
                type={SourceAndDestinationEnum.Source}
                sourceAccount={sourceAccount}
                onSourceAccountChange={handleSetSourceAccount}
              />
            )}
          </StyledAmountSelection>
        </>
      );
    }
    case PincodeState.FetchKeyboardAfterError:
    case PincodeState.PromptingKeyboard: {
      const keyboard = context.keyboard!;
      return (
        <PincodeKeyboard
          keyboard={keyboard}
          onSubmit={submitPincode}
          title={formatMessage("pincodeConfirmation.label")}
          errorMessage={errorMessage}
          loadingKeyboard={state === PincodeState.FetchKeyboardAfterError}
        />
      );
    }
    case TransferState.Done: {
      const transactionResult = context.transactionResult!;
      const recipient = {
        ...context.recipient,
        name: context.recipient?.name ?? "",
      };
      return (
        <StyledTransferSummary
          recipient={recipient}
          title={formatMessage("transferSummary.summaryTitle")}
          subtitle={
            context.transferMode === TransferMode.CashTransfer
              ? formatMessage("transferSummary.cashSummarySubtitle", { recipient: "recipient.name" })
              : formatMessage("transferSummary.accountSummarySubtitle")
          }
          middleStepLabel={formatMessage("transferSummary.summaryMiddleStep")}
          transaction={(transactionResult as TransactionRequest).metadata.transaction}
          onDone={closeModal}
          boldSubtitle={context.transferMode !== TransferMode.CashTransfer}
        />
      );
    }
    default: {
      return (
        <LoadingContainer>
          <MainColorSpinner size="30px" />
        </LoadingContainer>
      );
    }
  }
};

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledAmountSelection = styled(AmountSelection)`
  margin-top: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 30vh;
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin: 10px 50px;
  text-align: center;
`;

const StyledTransferSummary = styled(TransferSummary)<{ boldSubtitle?: boolean }>`
  ${Subtitle} {
    ${(props) => !props.boldSubtitle && theme.text}
  }
`;
